import React, { ReactElement, Ref, useState } from 'react';

import PropTypes from 'prop-types';

import { StyleProp, stylePropType } from '@adsk/alloy-react-theme';

import { MultiAutocompleteControlProps } from './MultiAutocompleteControl';
import { defaultLabelGetter } from '../helpers';
import { ControlLabel, Placeholder, SearchInput, Token } from '../layout';
import { MultiControlProps } from '../layout/Control/MultiControl';
import { GenericItem } from '../types';

const defaultRenderTokens = <TItem extends GenericItem>({
  selectedItems,
  itemToString,
  removeSelectedItem,
  getSelectedItemProps,
}: Pick<
  MultiControlProps<TItem>,
  | 'selectedItems'
  | 'itemToString'
  | 'removeSelectedItem'
  | 'getSelectedItemProps'
>) =>
  selectedItems?.map((selectedItem: TItem, index: number) => {
    const {
      style: tokenStyle,
      css: _ignored,
      ...selectedItemProps
    } = getSelectedItemProps({
      selectedItem,
      index,
    });

    return (
      <Token
        key={`selected-item-${index}`}
        style={tokenStyle as StyleProp}
        onClickX={() => removeSelectedItem(selectedItem)}
        {...selectedItemProps}
        content={itemToString?.(selectedItem)}
      />
    );
  });

const MultiAutocompleteTokenControlWrapped = <TItem extends GenericItem>(
  {
    selectedItems,
    isOpen,
    disabled,
    getInputProps,
    removeSelectedItem,
    getSelectedItemProps,
    itemToString = defaultLabelGetter,
    openMenu,
    renderItems = defaultRenderTokens,
    renderPlaceholder = (placeholderProps) => (
      <Placeholder {...placeholderProps} />
    ),
    style,
    placeholder,
    ...props
  }: MultiAutocompleteControlProps<TItem>,
  ref: Ref<HTMLDivElement | HTMLLabelElement>,
) => {
  const [inputStyle, setInputStyle] = useState({
    width: '0ch',
  });

  const hasSelectedItems = selectedItems?.length
    ? selectedItems.length > 0
    : false;

  const resizeInput = (value: string) => {
    setInputStyle({
      width: `${value.length}ch`,
    });
  };

  const {
    style: _ignoredStyle,
    css: _ignoredInputCss,
    onChange: inputOnChange,
    placeholder: inputPlaceholder,
    ...inputProps
  } = getInputProps?.({
    readOnly: !isOpen,
    onKeyDown: (e) => {
      if ((e.target as HTMLInputElement).value.length) {
        // there is text in the input, don't delete an item
        return;
      }

      if (e.key === 'Backspace' && selectedItems?.length) {
        removeSelectedItem(selectedItems[selectedItems?.length - 1]);
      }
    },
  }) || { style: undefined };

  const placeholderToUse = placeholder || inputPlaceholder;

  const handleInputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resizeInput(e.target.value);
    inputOnChange?.(e);
  };

  return (
    <ControlLabel
      ref={ref}
      isAutocomplete
      isOpen={isOpen}
      disabled={disabled}
      onClick={() => {
        !isOpen && !disabled && openMenu();
      }}
      style={[
        {
          maxHeight: '6.25em',
          overflow: 'hidden',
        },
        style,
      ]}
      {...props}
    >
      <div
        css={[
          {
            display: 'flex',
            overflowY: 'auto',
            flexWrap: 'wrap',
            flex: 1,
            maxHeight: 'inherit',
          },
        ]}
      >
        {selectedItems &&
          renderItems({
            isOpen,
            selectedItems,
            getSelectedItemProps,
            removeSelectedItem,
            itemToString,
          })}
        <SearchInput
          style={inputStyle}
          onChange={handleInputOnChange}
          showIcon={false}
          isOpen={isOpen}
          disabled={disabled}
          // don't show placeholder if we have tokens, otherwise use the given placeholder
          placeholder={hasSelectedItems ? '' : placeholderToUse}
          {...inputProps}
        />
        {!hasSelectedItems &&
          !isOpen &&
          renderPlaceholder({ children: placeholderToUse })}
      </div>
    </ControlLabel>
  );
};

const MultiAutocompleteTokenControl = React.forwardRef(
  MultiAutocompleteTokenControlWrapped,
);

MultiAutocompleteTokenControl.displayName = 'MultiAutocompleteTokenControl';

MultiAutocompleteTokenControl.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to get props for the menu toggle button */
  getToggleButtonProps: PropTypes.func.isRequired,
  /** Function to get props for the label surrounding the control */
  getLabelProps: PropTypes.func,
  /** The content of te ControlContainer */
  children: PropTypes.any,
};

export default MultiAutocompleteTokenControl as <T extends GenericItem>(
  p: MultiAutocompleteControlProps<T> & {
    ref?: Ref<HTMLDivElement | HTMLLabelElement>;
  },
) => ReactElement;
